/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ComponentRef, Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type Command, type Filter, type HttpResponse, type JobResult, type Rest, BatchService, HttpService, PromptService, Status } from '@dm/core';
import { type Snapshot, type SnapshotDeleteSendType, type SnapshotSendType } from 'app/common/types/mo';
import { type Observable, forkJoin } from 'rxjs';

import { type PromptTableComponent } from './prompt-table/prompt-table.component';
import { type SnapshotExpandMo, type SnapshotModifyMo, type SnapshotOperationMo, type SnapshotRestoreMo } from './snapshot-model';
import { SnapshotStrService } from './snapshot-str.service';

@Injectable({ providedIn: 'root' })
export class SnapshotService {
    constructor(
        public http: HttpService,
        private promptService: PromptService,
        private str: SnapshotStrService,
        private batch: BatchService,
    ) { }

    // 批量查询快照信息
    getSnapshotRest(parentId?: string): Rest {
        const rest = this.http.makeUrl('snapshot.getSnapshot', undefined, true).setFilters([{
            field: 'SUBTYPE',
            value: '0', // 普通LUN的快照
            matchMode: '::',
        }]);

        if (parentId) {
            rest.setFilters([
                { field: 'PARENTID', matchMode: '::', value: parentId },
            ]);
        }

        return rest;
    }

    /**
     * 查询源LUN下快照数量
     */
    getSnapshotCount(parentId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.getSnapshotCount', undefined, true).setFilters([{
            field: 'SUBTYPE',
            value: '0',
            matchMode: '::',
        }, { field: 'PARENTID', matchMode: '::', value: parentId }]);

        return this.http.get(rest);
    }

    // 单个查询
    get(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.get').format(id).setParams({ timeConversion: 0 });
        return this.http.get(rest, options);
    }

    // 取消回滚REST
    cancelrollbackREST(): Rest {
        return this.http.makeUrl('snapshot.cancelrollback');
    }

    // 取消回滚
    cancelrollback(rest: Rest, ID: string, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.http.put(rest, { ID }, opt);
    }

    // 删除快照
    deleteSnapshot(ID: string, params: SnapshotDeleteSendType): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.delete').format(ID);
        rest.setParams(params);
        return this.http.delete(rest, { doException: false });
    }

    // LUNREST
    getLUNRest(): Rest {
        return this.http.makeUrl('lun.getLUN', undefined, true);
    }

    /**
     * 查询可以作为快照源LUN的LUN
     */
    getLunRestSnapOnly(): Rest {
        return this.http.makeUrl('lun.getLUN', undefined, true).setParams({
            RSSTYPE: 22,
        });
    }

    /**
     * 快照源LUN或可以加入CDP的LUN。不能用RSSTYPE筛选，需要做LUN类型过滤
     * @returns
     */
    getLunRestSnapAndSchedule(): Rest {
        return this.http.makeUrl('lun.getLUN', undefined, true).setFilters(
            [
                // 0: 普通LUN； 6: 计算节点lun
                { field: 'SUBTYPE', matchMode: '::', value: '0_6' },
                // 排除容器LUN
                { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
            ],
        );
    }

    /**
     * 查询已经创建快照的LUN
     */
    getSnapshotLun(offset: number, limit: number, name?: string): Observable<[HttpResponse, HttpResponse]> {
        const rest = this.http.makeUrl('lun.getBatch', undefined, true)
            .setParams({
                RSSTYPE: 32,
                range: `[${offset}-${offset + limit}]`,
            });
        const countRest = this.http.makeUrl('lun.count', undefined, true).setParams({
            RSSTYPE: 32,
        });

        if (name) {
            const filter: Filter = {
                field: 'NAME',
                matchMode: ':',
                value: name,
            };
            rest.setFilters([filter]);
            countRest.setFilters([filter]);
        }

        return forkJoin([
            this.http.get(rest),
            this.http.get(countRest),
        ]);
    }

    getSnapshotLunWithCount(offset: number, limit: number, parentId: string): Observable<[HttpResponse, HttpResponse]> {
        const rest = this.getSnapshotRest(parentId).setRange({ limit, offset });

        const countRest = this.http.makeUrl('snapshot.count', undefined, true).setFilters([
            {
                field: 'PARENTID',
                matchMode: '::',
                value: parentId,
            },
            {
                field: 'SUBTYPE',
                value: '0', // 普通LUN的快照
                matchMode: '::',
            },
        ]);

        return forkJoin([
            this.http.get(rest),
            this.http.get(countRest),
        ]);
    }

    /**
     * 批量查询级联快照
     */
    getSnapshotsByParentId(parentId: string): Observable<HttpResponse> {
        const rest = this.getSnapshotRest(parentId);
        return this.http.get(rest);
    }

    // 创建快照REST
    createSnapshotRest(): Rest {
        return this.http.makeUrl('snapshot.create');
    }

    // 创建快照OSB对象
    createSnapshot(rest: Rest, body: SnapshotSendType, opt: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.http.post(rest, body, opt);
    }

    // 批量创建快照
    createSnapshotBatch(body: SnapshotSendType[], opt: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.createBatch');
        return this.http.post(rest, body, opt);
    }

    // 修改快照REST
    modifySnapshotRest(ID: string): Rest {
        return this.http.makeUrl('snapshot.modify').format(ID);
    }

    // 修改快照
    modifySnapshot(rest: Rest, body: SnapshotModifyMo, opt: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.http.put(rest, body, opt);
    }

    // 回滚快照REST
    restoreSnapshotRest(): Rest {
        return this.http.makeUrl('snapshot.snapshotRollback');
    }

    // 回滚快照
    restoreSnapshot(rest: Rest, params: SnapshotRestoreMo, opt: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.http.put(rest, params, opt);
    }

    // 扩容快照Rest
    expansionRest(): Rest {
        return this.http.makeUrl('snapshot.expansion');
    }

    // 扩容快照
    expandSnapshot(body: SnapshotExpandMo, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.expansion');
        return this.http.put(rest, body, opt);
    }

    // 扩容LUN
    expandLun(body: SnapshotExpandMo, opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('lun.expand');
        return this.http.put(rest, body, opt);
    }

    // 扩容LUNRest
    expansionLUNRest(): Rest {
        return this.http.makeUrl('lun.expand');
    }

    // 扩容LUN
    expansionLUN(rest: Rest, body: SnapshotExpandMo, opt: BaseRequestOptionsArgs): Observable<HttpResponse> {
        return this.http.put(rest, body, opt);
    }

    // 查询LUN信息
    getLUN(id: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('lun.get').format(id).setParams({ timeConversion: 0 });
        return this.http.get(rest);
    }

    // 查询快照一致性组名称
    getSnapshotCg(id: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshotCg.get').format(id);
        return this.http.get(rest);
    }

    /**
     * 重建
     */
    reconstruct(params: SnapshotOperationMo, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.reconstruct');
        return this.http.put(rest, params, this.setReqTimeout(options));
    }

    /**
     * 批量查询HyperCDPrest
     */
    getBatchCdpRest(parentId: string, vstoreId?: string): Rest {
        return this.http.makeUrl('hyperCdp.getBatch').setParams({ vstoreId })
            .setFilters([{ field: 'PARENTID', matchMode: '::', value: parentId }]);
    }

    /**
     * 获取查询其他可以回滚的快照的rest
     * @param rssAssociateObjId
     */
    getOtherSnapshotRest(rssAssociateObjId: string): Rest {
        return this.http.makeUrl('snapshot.getBatch').setParams({
            RSSTYPE: 33,
            RSSASSOCIATEOBJID: rssAssociateObjId,
        });
    }

    /**
     * 获取查询其他可以回滚的快照的rest
     * @param rssAssociateObjId
     */
    getOtherSnapshotCount(rssAssociateObjId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.count').setParams({
            RSSTYPE: 33,
            RSSASSOCIATEOBJID: rssAssociateObjId,
        });
        return this.http.get(rest);
    }

    /**
     * 创建副本
     * @param params
     * @param options
     */
    createDuplicate(params: Snapshot, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshotCg.createDuplicate');
        return this.http.post(rest, params, this.setReqTimeout(options, 250));
    }

    setReqTimeout(options: BaseRequestOptionsArgs, sec = 130): BaseRequestOptionsArgs {
        return {
            ...options,
            timeout: sec * 1000,
        };
    }

    // 激活快照
    active(params: SnapshotOperationMo, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.active');
        return this.http.post(rest, params, option);
    }

    // 通过HyperCDP激活快照
    activeByCdp(params: SnapshotOperationMo, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.activeByCdp');
        return this.http.post(rest, params, option);
    }

    // 去激活快照
    deactive(id: string, option?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('snapshot.deactive');
        return this.http.put(rest, { ID: id }, option);
    }

    /**
     * 处理激活快照动作
     */
    activeAction(items: Snapshot[], promptTable: ComponentRef<PromptTableComponent>, callback: (isRefresh: boolean) => void, cdpId?: string): void {
        this.promptService.warning({
            id: 'prompt-lun-snapshot-activeAction',
            title: this.str.table.activateTitle,
            width: 700,
            content: this.str.table.activateTips,
            templateRef: promptTable,
            canHidden: true,
            onOk: () => {
                this.promptService.close();

                const cmds = items.map((item): Command => ({
                    name: this.str.activeTip(item.NAME),
                    action: cdpId ?
                        this.activeByCdp({ ID: item.ID, CDPID: cdpId }) :
                        this.active({ ID: item.ID }, { timeout: 150 * 1000 }),
                }));

                this.batch.exePrompt(cmds, {
                    onClose: (resArr: JobResult[]) => callback(resArr.some((a) => a.status === Status.success)),
                });
            },
        });
    }

    /**
     * 处理取消激活快照动作
     * @param items
     * @param promptTable
     * @param callback
     */
    deactiveAction(items: Snapshot[], promptTable: ComponentRef<PromptTableComponent>, callback: (isRefresh: boolean) => void): void {
        this.promptService.danger({
            title: this.str.table.deactiveTitle,
            width: 700,
            content: this.str.table.deactiveTips,
            templateRef: promptTable,
            onOk: () => {
                this.promptService.close();

                const cmds = items.map((item): Command => ({
                    name: this.str.deactiveTip(item.NAME),
                    action: this.deactive(item.ID, { doException: false }),
                }));

                this.batch.exePrompt(cmds, {
                    onClose: (resArr: JobResult[]) => callback(resArr.some((a) => a.status === Status.success)),
                });
            },
        });
    }

    /**
     * 获取目标LUNRest
     */
    getTargetLunRest(id: string, vstoreId?: string): Rest {
        const rest = this.http.makeUrl('lun.getTargetLun', undefined, true).setParams({
            RSSType: 51,
            RSSAssociateObjID: id,
            vstoreId,
        });
        return rest;
    }

    /**
     * 获取所有LUNRest
     */
    getAllLunRest(): Rest {
        const rest = this.http.makeUrl('lun.getBatch');
        return rest;
    }
}
